import React, { useState, useEffect, useContext } from 'react';
import { PRISMAX_BACKEND_URL } from '../../../configs/config';
import styles from './FleetV2.module.css';
import RobotModalV2 from './RobotModalV2';
import PurchaseInfoModal from './PurchaseInfoModal';
import UpgradeMembershipModal from './UpgradeMembershipModal';
import { SwapMainAppContext } from '../../../context/SwapMainAppContext';
import { useLocation, useNavigate } from 'react-router-dom';

import piperImg from '../../../static_files/fleet-robot-piper.png'
import tok2Img from '../../../static_files/fleet-robot-tok2.png';
import yamImg from '../../../static_files/fleet-robot-yam.png';
import useBackgroundColorChange from '../../../utils/useBackgroundColorChange';

const ROBOT_IMAGES = {
    Piper: piperImg,
    TOK2: tok2Img,
    YAM: yamImg,
};

const IMAGE_CLASS = {
    Piper: styles.cardImgPiper,
    TOK2: styles.cardImgTok2,
    YAM: styles.cardImgYam,
};

const EST_EARNINGS_PER_WEEK = 1200;

function ArrowIcon() {
    return (
        <svg className={styles.ctaArrow} width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2 6h8M6.5 2.5L10 6l-3.5 3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

export default function FleetV2() {
    const [robots, setRobots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedRobot, setSelectedRobot] = useState(null);
    const [showPurchaseModal, setShowPurchaseModal] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [isPaymentRedirect, setIsPaymentRedirect] = useState(false);
    const [robotForPurchase, setRobotForPurchase] = useState(null);

    const { gatewayToken, userClass, showNotification } = useContext(SwapMainAppContext);
    const location = useLocation();
    const navigate = useNavigate();
    useBackgroundColorChange('#181614')

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        if (searchParams.get('payment') === 'success' && searchParams.has('session_id')) {
            setShowUpgradeModal(true);
            setIsPaymentRedirect(true);
            navigate(location.pathname, { replace: true });
        }
    }, [location.search, navigate]);

    const handleBuyNow = (robot) => {
        if (!gatewayToken) {
            showNotification('warning', 'Please log in first to continue your purchase.')
            return;
        }
        if (userClass === 'Explorer Member') {
            setRobotForPurchase(robot);
            setSelectedRobot(null);
            setShowUpgradeModal(true);
            return;
        }
        setRobotForPurchase(robot);
        setSelectedRobot(null);
        setShowPurchaseModal(true);
    };

    const handleUpgradeClose = () => {
        setShowUpgradeModal(false);
        setIsPaymentRedirect(false);
    };

    const handleUpgradeSuccess = () => {
        setShowUpgradeModal(false);
        setIsPaymentRedirect(false);
        if (robotForPurchase) {
            setShowPurchaseModal(true);
        }
    };

    useEffect(() => {
        fetch(`${PRISMAX_BACKEND_URL}/api/fleet/get-available-robots`)
            .then((res) => {
                if (!res.ok) throw new Error('Failed to load fleet data.');
                return res.json();
            })
            .then((json) => {
                if (!json.success) throw new Error('Unexpected response from server.');
                const sorted = [...json.data].sort((a, b) => a.fleet_order - b.fleet_order);
                setRobots(sorted);
            })
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
    }, []);

    // Deep link support: /data/fleet#<machine_id> opens that robot's modal
    // once the fleet has loaded. If the hash doesn't match any fetched
    // (fleet-visible) robot, the page just loads normally with no modal.
    useEffect(() => {
        if (!robots.length) return;
        const hashId = location.hash.replace('#', '');
        if (!hashId) return;
        const match = robots.find((r) => String(r.machine_id) === hashId);
        if (match) setSelectedRobot(match);
    }, [robots, location.hash]);

    return (
        <div className={styles.outer}>
            <div className={styles.main}>
                <div className={styles.hero}>
                    <h1 className={styles.heroTitle}>Validated hardware. Verified data.</h1>
                    <p className={styles.heroSubtitle}>
                        Every platform below has passed setup validation and meets PrismaX quality standards.{' '}
                        <strong>Tap any robot to see full specs and buy it</strong> for your next collection run.
                    </p>
                </div>

                {loading && (
                    <div className={styles.stateContainer}>Loading fleet…</div>
                )}

                {error && (
                    <div className={styles.stateContainer}>{error}</div>
                )}

                {!loading && !error && (
                    <div className={styles.fleet}>
                        {robots.map((robot) => {
                            const imgSrc = ROBOT_IMAGES[robot.product_name];
                            const imgCls = IMAGE_CLASS[robot.product_name] || '';
                            const price = parseFloat(robot.fleet_purchase_price);

                            return (
                                <div
                                    key={robot.machine_id}
                                    className={styles.card}
                                    onClick={() => setSelectedRobot(robot)}
                                >
                                    <div className={styles.cardHead}>
                                        <div>
                                            <div className={styles.cardName}>{robot.product_name}</div>
                                            <div className={styles.cardMaker}>{robot.manufacture}</div>
                                        </div>
                                        <span className={styles.badge}>
                                            <span className={styles.badgeDot} />
                                            Validated
                                        </span>
                                    </div>

                                    <div className={styles.cardImgWrap}>
                                        {imgSrc && (
                                            <img
                                                src={imgSrc}
                                                alt={robot.product_name}
                                                className={`${styles.cardImg} ${imgCls}`}
                                            />
                                        )}
                                    </div>

                                    <div className={styles.chips}>
                                        {robot.fleet_selling_points?.map((pt) => (
                                            <span key={pt} className={styles.chip}>{pt}</span>
                                        ))}
                                    </div>

                                    <div className={styles.cardPrice}>
                                        <div className={styles.priceCol}>
                                            <span className={styles.priceVal}>
                                                ${price.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                                            </span>
                                            <span className={styles.priceLbl}>Includes setup &amp; onboarding</span>
                                        </div>
                                        <div className={`${styles.priceCol} ${styles.priceColRight}`}>
                                            <span className={styles.earnVal}>
                                                ~${EST_EARNINGS_PER_WEEK.toLocaleString()}<span className={styles.earnPer}>/wk</span>
                                            </span>
                                            <span className={styles.priceLbl}>Est. operator earnings</span>
                                        </div>
                                    </div>

                                    <button className={styles.cardCta}>
                                        View details &amp; buy
                                        <ArrowIcon />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {selectedRobot && (
                <RobotModalV2
                    robot={selectedRobot}
                    onClose={() => setSelectedRobot(null)}
                    onBuyNow={() => handleBuyNow(selectedRobot)}
                />
            )}

            {showUpgradeModal && (
                <UpgradeMembershipModal
                    onClose={handleUpgradeClose}
                    onGoBack={handleUpgradeClose}
                    paymentSuccess={isPaymentRedirect}
                    onContinue={handleUpgradeSuccess}
                />
            )}

            {showPurchaseModal && robotForPurchase && (
                <PurchaseInfoModal
                    onClose={() => { setShowPurchaseModal(false); setRobotForPurchase(null); }}
                    onGoBack={() => { setShowPurchaseModal(false); setSelectedRobot(robotForPurchase); }}
                    robotName={robotForPurchase.product_name}
                    robotImage={ROBOT_IMAGES[robotForPurchase.product_name]}
                    machineId={robotForPurchase.machine_id}
                />
            )}
        </div>
    );
}
